import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  clearRefreshToken,
  refreshAccessTokenApi,
  sendPortalEvent,
  PORTAL_EVENT_TYPES,
} from '../helpers/apiHelper';
import { clearAnnouncementShown } from '../helpers/announcementSession';
import { getDisplayNameFromToken } from '../helpers/userNameHelper';

const AuthContext = createContext();
const SESSION_TOKENS_KEY = 'cocoPortal.sessionTokens';

const buildUserFromIdToken = (idToken, previousUser = null) => {
  const decodedToken = jwtDecode(idToken);
  return {
    ...previousUser,
    email: decodedToken.email || previousUser?.email || null,
    username: decodedToken.email || previousUser?.username || previousUser?.email || null,
    userId: decodedToken['custom:portalUserId'] || decodedToken.sub || previousUser?.userId || null,
    portalUserId: decodedToken['custom:portalUserId'] || previousUser?.portalUserId || null,
    cognitoUserId: decodedToken.sub || previousUser?.cognitoUserId || null,
    phone: decodedToken.phone_number || previousUser?.phone || null,
    name: getDisplayNameFromToken(decodedToken) || previousUser?.name || null,
  };
};

const getStoredTokens = () => {
  try {
    const stored = sessionStorage.getItem(SESSION_TOKENS_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    return null;
  }
};

const storeSessionTokens = (tokens) => {
  try {
    sessionStorage.setItem(SESSION_TOKENS_KEY, JSON.stringify(tokens));
  } catch (error) {
    // Authentication remains usable for the current page when storage is unavailable.
  }
};

const clearStoredTokens = () => {
  try {
    sessionStorage.removeItem(SESSION_TOKENS_KEY);
  } catch (error) {
    // Signing out must not depend on browser storage availability.
  }
};

// Refreshes closer together than this are dropped, so nothing can turn into a refresh loop.
const MIN_REFRESH_INTERVAL_MS = 10 * 1000;

// Expiry is measured on the browser's own clock from the moment the token arrived, using the
// lifetime the server issued (exp - iat). A PC clock that is wrong by any amount therefore cannot
// make a freshly issued token look expired, which used to loop /auth/refresh forever (TDDS-3515).
const withLocalExpiry = (tokens) => {
  try {
    const { iat, exp } = jwtDecode(tokens.idToken);
    const expiresAt = Number.isFinite(iat) && Number.isFinite(exp)
      ? Date.now() + (exp - iat) * 1000
      : exp * 1000;
    return { ...tokens, expiresAt };
  } catch (error) {
    return { ...tokens, expiresAt: 0 };
  }
};

const getExpiresAt = (tokens) => {
  if (Number.isFinite(tokens?.expiresAt)) return tokens.expiresAt;
  try {
    return jwtDecode(tokens.idToken).exp * 1000;
  } catch (error) {
    return 0;
  }
};

const hasExpired = (tokens) => getExpiresAt(tokens) <= Date.now();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tokens, setTokens] = useState({
    idToken: null,
    accessToken: null,
  });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  const refreshInterval = useRef(null);
  const userRef = useRef(user);
  userRef.current = user;
  const tokensRef = useRef(tokens);
  tokensRef.current = tokens;
  const refreshInFlight = useRef(null);
  const lastRefreshAt = useRef(0);

  const signOut = useCallback(async ({ clearRefreshCookie = true, reason } = {}) => {
    // Sent before any token is cleared; fire-and-forget so it never delays sign-out.
    const idToken = tokensRef.current?.idToken;
    if (idToken) {
      sendPortalEvent(PORTAL_EVENT_TYPES.LOGOUT, {
        idToken,
        metadata: { reason: reason || (clearRefreshCookie ? 'USER' : 'SESSION_EXPIRED') },
      });
    }

    if (clearRefreshCookie) {
      try {
        await clearRefreshToken();
      } catch (err) {
        // console.error('Error clearing refresh token during sign out:', err);
      }
    }

    clearAnnouncementShown(userRef.current);
    setUser(null);
    setTokens({ idToken: null, accessToken: null});
    setIsLoggedIn(false);
    setLoading(false);
    localStorage.removeItem("wasLoggedIn"); 
    clearStoredTokens();

    if (refreshInterval.current) {
      clearInterval(refreshInterval.current);
      refreshInterval.current = null;
    }
  }, []);

  const refreshAccessToken = useCallback(async () => {
    if (sessionExpired) return;
    if (refreshInFlight.current) return refreshInFlight.current;
    if (Math.abs(Date.now() - lastRefreshAt.current) < MIN_REFRESH_INTERVAL_MS) return;

    lastRefreshAt.current = Date.now();
    refreshInFlight.current = (async () => {
      try {
        const res = await refreshAccessTokenApi();
        const authenticationResult = res?.AuthenticationResult;
        const idToken =
          res?.id_token ||
          res?.idToken ||
          res?.IdToken ||
          authenticationResult?.IdToken ||
          authenticationResult?.id_token ||
          null;
        if (idToken) {
          const nextTokens = withLocalExpiry({
            idToken,
            accessToken:
              res.access_token ||
              res.AccessToken ||
              authenticationResult?.AccessToken ||
              tokensRef.current.accessToken ||
              null,
          });
          setUser(prev => buildUserFromIdToken(idToken, prev));
          setTokens(nextTokens);
          storeSessionTokens(nextTokens);
          setIsLoggedIn(true);
        } else {
          setSessionExpired(true);
          signOut({ clearRefreshCookie: false });
        }
      } catch (err) {
        setSessionExpired(true);
        signOut({ clearRefreshCookie: false });
      } finally {
        refreshInFlight.current = null;
      }
    })();
    return refreshInFlight.current;
  }, [signOut, sessionExpired]);

  const refreshAccessTokenRef = useRef(refreshAccessToken);
  refreshAccessTokenRef.current = refreshAccessToken;

  const signIn = (userData, tokenData) => {
    localStorage.setItem("wasLoggedIn", "true");
    setSessionExpired(false);
    setUser(userData);
    
    const nextTokens = withLocalExpiry({
      idToken: tokenData.IdToken,
      accessToken: tokenData.AccessToken,
    });
    setTokens(nextTokens);
    storeSessionTokens(nextTokens);
    setIsLoggedIn(true);
    setLoading(false);
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);

      const storedTokens = getStoredTokens();
      if (storedTokens?.idToken && !hasExpired(storedTokens)) {
        setUser(buildUserFromIdToken(storedTokens.idToken));
        setTokens(storedTokens);
        setIsLoggedIn(true);
        setSessionExpired(false);
        setLoading(false);
        return;
      }

      clearStoredTokens();
      const wasLoggedIn = localStorage.getItem("wasLoggedIn");

      if (wasLoggedIn === "true") {
        try {
          await refreshAccessTokenRef.current();
        } catch (err) {
        }
      }

      setLoading(false);
    };

    // Runs once on mount. Re-running it whenever the refresh callback changed restarted the
    // session check after every refresh (TDDS-3515).
    init();
  }, []);

  useEffect(() => {
    if (!tokens.idToken) {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
        refreshInterval.current = null;
      }
      return;
    }

    if (refreshInterval.current) {
      clearInterval(refreshInterval.current);
      refreshInterval.current = null;
    }

    refreshInterval.current = setInterval(() => {
      try {
        jwtDecode(tokensRef.current.idToken);
        const timeLeft = getExpiresAt(tokensRef.current) - Date.now();
        if (timeLeft < 2 * 60 * 1000) {
          refreshAccessToken();
        }
      } catch (e) {
        console.error('[Auth] Failed to decode ID token during interval check', e);
        signOut({ reason: 'INVALID_TOKEN' });
      }
    }, 60 * 1000);

    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
        refreshInterval.current = null;
      }
    };
  }, [tokens.idToken, refreshAccessToken, signOut]);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        tokens,
        isLoggedIn,
        signIn,
        signOut,
        refreshAccessToken,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
